import React, { useState } from "react";
import ShareModal from "../../sections/modal-button/ShareModal";
import { removeResources, saveResources } from "../../utils/api";
import { useStore } from "@nanostores/react";
import $auth from "../../store/user";

interface CardData {
  name: string;
  id?: number;
  type?: string;
  institute_name: string;
  fee?: number | null;
  duration?: string;
  logo: string;
  city?: string;
  state?: string;
  country?: string;
  city_name?: string;
  country_name?: string;
  state_name?: string;
  course_slug?: string;
  course_fee_id?: number;
  slug?: string;
  short_course_slug?: string;
  duration_id?: number;
  short_course_fee_id?: number;
  course_scale_id?: number;
  institute_slug?: string;
  campuses?: Campus[];
}

interface Campus {
  id: number;
  campus: string;
  address: string;
}
export interface Course {
  id: number;
  name: string;
  fee: number | null;
  institute_name: string;
  logo: string;
  course_slug?: string | null;
  duration: string;
  duration_id?: number;
  course_fee_id?: number;
  institute_slug?: string;
}
interface CardConfig {
  icon: React.ComponentType<{ className?: string }>;
  detailsConfig: {
    label: string;
    value: string | number;
    key: string;
  }[];
  buttons: {
    primary?: {
      label: string;
      href: string;
    };
    secondary?: {
      label: string;
      href: string;
    };
  };
  actionIcons?: React.ComponentType<{ className?: string }>[];
  tooltips?: string[];
  pageType?:
    | "course"
    | "scholarship"
    | "university"
    | "short-course"
    | "savecourse";
}

interface SearchCardProps {
  data: CardData;
  config: CardConfig;

  defaultImage?: string;
  gridClassName?: string;
  isAlreadySaved?: boolean;
  variant?: "default" | "university" | "uni-detail";
  removeBorderAfterMobileFourth?: boolean;
  handleCompareClick: (newCourse: Course) => void;
  onRemove?: () => void;
  onSave?: () => void;
  institute_slug?: string;
}

const SearchCard: React.FC<SearchCardProps> = ({
  data,
  config,
  defaultImage,
  gridClassName = "grid-cols-3 xl:grid-cols-6",
  variant = "default",
  isAlreadySaved = false,
  removeBorderAfterMobileFourth = false,
  handleCompareClick,
  onRemove,
  onSave,
}) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const auth = useStore($auth);

  async function SaveResourse() {
    try {
      if (data.id !== undefined) {
        if (data.type && !isAlreadySaved) {
          const response = await saveResources(data.id, data.type);
          if (response && onSave) {
            onSave();
          }
        }
      }
    } catch (error) {
      console.warn(error);
    }
  }

  async function RemoveResourse() {
    try {
      if (data.id !== undefined) {
        if (data.type && isAlreadySaved) {
          const response = await removeResources(data.id, data.type);
          if (response && onRemove) {
            onRemove();
          }
        }
      }
    } catch (error) {
      console.warn(error);
    }
  }

  const handSaveClick = () => {
    if (!auth.isAuthenticated) {
      window.dispatchEvent(
        new CustomEvent("openAuthModal", {
          detail: { modalType: data.type }, // Or any other modal type you want
        }),
      );
      return;
    }

    if (isAlreadySaved) {
      RemoveResourse();
    } else {
      SaveResourse();
    }
  };

  const {
    icon: IconComponent,
    detailsConfig,
    buttons,
    actionIcons = [],
    pageType,
  } = config;

  const tooltipLabels =
    config.tooltips ||
    (config.actionIcons?.length === 3
      ? isAlreadySaved
        ? ["Remove", "Add to list", "Share"]
        : ["Save", "Add to list", "Share"]
      : isAlreadySaved
        ? ["Remove", "Share"]
        : ["Save", "Share"]);

  const getShareUrl = () => {
    const baseUrl = window.location.origin;

    switch (pageType) {
      case "course":
        if (data.course_slug && data.duration_id && data.course_fee_id) {
          return `${baseUrl}/course/${data.course_slug}/${data.duration_id}/${data.course_fee_id}`;
        }
        return buttons.secondary?.href || window.location.href;

      case "savecourse":
        if (data.slug && data.duration_id && data.course_fee_id) {
          return `${baseUrl}/course/${data.slug}/${data.duration_id}/${data.course_fee_id}`;
        }
        return buttons.secondary?.href || window.location.href;

      case "scholarship":
        if (data.slug) {
          return `${baseUrl}/scholarship/${data.slug}`;
        }
        return (
          `${window.location.origin}${buttons.secondary?.href}` ||
          window.location.href
        );

      case "university":
        if (data.slug) {
          return `${baseUrl}/university-detail/${data.slug}`;
        }
        return buttons.secondary?.href || window.location.href;

      case "short-course":
        if (
          data.short_course_slug &&
          data.duration_id &&
          data.course_scale_id &&
          data.short_course_fee_id
        ) {
          return `${baseUrl}/short-course/${data.short_course_slug}/${data.duration_id}/${data.course_scale_id}/${data.short_course_fee_id}`;
        }
        return buttons.secondary?.href || window.location.href;

      default:
        return window.location.href;
    }
  };

  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  const DefaultHeader = () => (
    <>
      <h3 className="text-base xl:text-md font-semibold xl:mt-0 mt-2">
        {data.name}
      </h3>
      <a
        href={
          variant === "uni-detail"
            ? `/university-detail/${data.institute_slug}`
            : `university-detail/${data.institute_slug}`
        }
        className="hover:text-primary text-body text-sm flex items-center mt-3"
        target="_blank"
        rel="noreferrer"
      >
        <IconComponent className="mr-1" />
        {data.institute_name}
      </a>
    </>
  );

  const UniversityHeader = () => (
    <>
      <a
        href={
          variant === "uni-detail"
            ? `/university-detail/${data.institute_slug}`
            : `university-detail/${data.institute_slug}`
        }
        className="text-body text-sm flex items-center mt-3  "
        target="_blank"
        rel="noreferrer"
      >
        <h3 className="text-base xl:text-md font-semibold xl:mt-0 mt-2 hover:text-primary">
          {data.institute_name}
        </h3>
      </a>

      {data?.city_name && data?.state_name && data?.country_name && (
        <a
          href={
            variant === "uni-detail"
              ? `/university-detail/${data.institute_slug}`
              : `university-detail/${data.institute_slug}`
          }
          className="text-body text-sm flex items-center mt-3 hover:text-primary "
          target="_blank"
          rel="noreferrer"
        >
          <IconComponent className="mr-1" />
          {data.city_name}, {data.state_name}, {data.country_name}
        </a>
      )}
    </>
  );

  return (
    <>
      <section className="md:px-4 px-2 max-w-full mx-auto w-full">
        <div className="mt-7 border bg-white border-border rounded-lg md:p-4 p-3 flex flex-col xl:flex-row items-start xl:items-center space-y-4 xl:space-y-0 xl:space-x-2">
          {/* Logo Section */}
          <div className="flex xl:flex-col w-full xl:w-auto items-start xl:items-center">
            <a
              href={
                variant === "uni-detail"
                  ? `/university-detail/${data.institute_slug}`
                  : `university-detail/${data.institute_slug}`
              }
              target="_blank"
              className="cursor-pointer"
              rel="noreferrer"
            >
              <img
                src={data.logo || defaultImage}
                alt={variant === "university" ? data.institute_name : data.name}
                className="flex-shrink-0 xl:w-32 xl:h-auto w-20 h-20 object-cover rounded hover:opacity-80 transition-opacity"
              />
            </a>

            <div className="ml-4 xl:hidden">
              {variant === "university" ? (
                <UniversityHeader />
              ) : (
                <DefaultHeader />
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 w-full">
            <div className="hidden xl:block">
              {variant === "university" ? (
                <UniversityHeader />
              ) : (
                <DefaultHeader />
              )}
            </div>

            {/* Details Grid */}
            <div
              className={`grid ${gridClassName} gap-x-2 mt-4 relative xl:mb-0 mb-2 gap-y-4`}
            >
              {detailsConfig.map((detail, idx) => (
                <div
                  key={detail.key}
                  className="flex justify-around items-start md:items-center relative"
                >
                  <div>
                    <p className="text-body font-normal text-xs leading-[20px]">
                      {detail.label}
                    </p>
                    <p className="text-title font-bold text-xs leading-[20px]">
                      {detail.value}
                    </p>
                  </div>
                  {idx < detailsConfig.length - 1 && (
                    <div
                      className={`top-0 right-0 h-[90%] border-r border-line 
                      ${removeBorderAfterMobileFourth && idx % 4 === 3 ? "hidden md:block" : ""}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            {buttons.primary && (
              <a
                href={buttons.primary.href}
                target="_blank"
                className="flex items-center text-center justify-center bg-primary hover:bg-primary-800 text-white px-[6px] sm:px-2 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm"
                rel="noreferrer"
              >
                {buttons.primary.label}
              </a>
            )}

            {buttons.secondary && (
              <a
                href={buttons.secondary.href}
                target="_blank"
                className="flex items-center text-center justify-center border border-primary hover:bg-primary-100 text-primary px-[6px] py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm"
                rel="noreferrer"
              >
                {buttons.secondary.label}
              </a>
            )}

            {actionIcons.map((ActionIcon, index) => {
              const handleIconClick = () => {
                if (actionIcons.length === 3) {
                  if (ActionIcon === config.actionIcons?.[2]) {
                    handleShareClick();
                  } else if (ActionIcon === config.actionIcons?.[1]) {
                    handleCompareClick({
                      id: data.course_fee_id || 0,
                      name: data.name,
                      fee: data.fee ?? null,
                      duration: data.duration ? data.duration : "",
                      institute_name: data.institute_name,
                      logo: data.logo || defaultImage || "",
                      course_slug: data.course_slug,
                      duration_id: data.duration_id,
                      course_fee_id: data.course_fee_id,
                      institute_slug: data.institute_slug,
                    });
                  } else if (ActionIcon === config.actionIcons?.[0]) {
                    handSaveClick();
                  }
                } else {
                  if (ActionIcon === config.actionIcons?.[1]) {
                    handleShareClick();
                  } else if (ActionIcon === config.actionIcons?.[0]) {
                    handSaveClick();
                  }
                }
              };

              return (
                <div key={index} className="relative group flex items-center">
                  <button
                    className={`hover:scale-110 transition-transform rounded-full ${
                      index === 0 && isAlreadySaved
                        ? "border-none p-0"
                        : "border border-primary p-2"
                    }`}
                    onClick={handleIconClick}
                  >
                    <ActionIcon />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none">
                      <div className="bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap">
                        {tooltipLabels[index]}
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareUrl={getShareUrl()}
      />
    </>
  );
};

export default SearchCard;
